import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { take } from 'rxjs';
import { AddUserListToWorkspaceResponse } from '../../../../../../backend/src/core/workspace/workspace.gate';
import { BoardService } from '../../../core/board/board.service';
import { CoreItemService } from '../../../core/core-item/core-item.service';
import { StripeService } from '../../../core/stripe/stripe.service';
import { WorkspaceHolderService } from '../../../core/workspace/workspace.holder.service';
import { surveyAnimations } from '../../../shared/animation/survey.animation';
import { BoardIconTitleComponent } from '../../board/board-icon-title/board-icon-title.component';

@Component({
  selector: 'app-invite-team-member-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzRadioModule,
    NzSelectModule,
    NzSegmentedModule,
    NzPopconfirmModule,
    NzDividerModule,
    NzProgressModule,
    NzCheckboxModule,
    NzToolTipModule,
    MatTooltipModule,

    BoardIconTitleComponent,
  ],
  templateUrl: './invite-team-member-list.component.html',
  styleUrl: './invite-team-member-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: surveyAnimations,
})
export class InviteTeamMemberListComponent {
  @Input() isVisible = false;
  @Output() isVisibleChange = new EventEmitter<boolean>();

  constructor(
    private stripeService: StripeService,
    private coreItemService: CoreItemService,
    private boardService: BoardService,
    private messageService: NzMessageService,
    private workspaceHolderService: WorkspaceHolderService
  ) {}

  isLoading = signal<boolean>(false);

  userList = toSignal(this.boardService.getAllUserList$(), { initialValue: [] });

  pendingEmailList = computed(() => {
    return this.workspaceHolderService.currentWorkspace().pendingEmailList ?? [];
  });

  get availableMemberCount() {
    return this.stripeService.availableMemberCount();
  }

  onCancelClick() {
    this.isVisible = false;
    this.isVisibleChange.emit(this.isVisible);
  }

  onIsVisibleChange(isVisible: boolean) {
    this.isVisibleChange.emit(isVisible);
  }

  currentStepIndex = signal<number>(0);

  readonly totalSteps = 1;
  currentProgress = computed(() => {
    return Math.round((this.currentStepIndex() / this.totalSteps) * 100);
  });

  emailList = signal<string[]>(['']);

  onAddEmailClick() {
    const restCount = this.availableMemberCount - this.userList().length;
    if (this.emailList().length === this.availableMemberCount - this.userList().length) {
      this.messageService.error(`You can only add up to ${restCount} email(s).`, { nzDuration: 8000 });
      return;
    }
    this.emailList.update((emailList) => {
      emailList.push('');
      return [...emailList];
    });
  }

  onDeleteEmailClick(i: number) {
    this.emailList.update((emailList) => {
      emailList.splice(i, 1);
      return [...emailList];
    });
  }

  addUserListResponse = signal<AddUserListToWorkspaceResponse | undefined>(undefined);

  onInviteClick() {
    const nonEmptyEmailList = this.emailList().filter((email) => email !== '');
    for (const email of nonEmptyEmailList) {
      if (!this.isValidEmail(email)) {
        this.messageService.error(`"${email}" is not a valid email address`, { nzDuration: 8000 });
        return;
      }
    }

    this.isLoading.set(true);

    this.workspaceHolderService
      .inviteMemberList$(nonEmptyEmailList)
      .pipe(take(1))
      .subscribe((response: AddUserListToWorkspaceResponse) => {
        this.addUserListResponse.set(response);

        const addedBoard: any[] = [];

        response.addedBoardList?.forEach((c) => {
          addedBoard.push(this.coreItemService.updateCoreItemInCache(c));
        });
        this.boardService.emitBoardChangesInCache(addedBoard);

        this.isLoading.set(false);
        this.currentStepIndex.set(this.currentStepIndex() + 1);
      });
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}
